<script setup lang="ts">
import { useI18n } from 'vue-i18n'

import AppIcon from '@/components/common/AppIcon.vue'
import { useUiStore } from '@/stores/ui'
import type { NotificationType } from '@/stores/ui'

/**
 * Toast host, pinned to the top of the viewport. API errors land here as red
 * alerts and auto-dismiss after 5 seconds (handled by the store).
 */
const ui = useUiStore()
const { t } = useI18n()

const STYLES: Record<NotificationType, { wrapper: string; icon: string }> = {
  error: { wrapper: 'border-red-800 bg-red-600 text-white', icon: 'mdi-alert-circle' },
  success: { wrapper: 'border-emerald-800 bg-emerald-600 text-white', icon: 'mdi-check-circle' },
  info: { wrapper: 'border-primary-800 bg-primary-600 text-white', icon: 'mdi-information' },
}
</script>

<template>
  <div
    class="pointer-events-none fixed inset-x-0 top-4 z-100 flex flex-col items-center gap-2 px-4"
  >
    <!--
      No <TransitionGroup> here, and that is a correctness requirement rather
      than a style choice. An enter transition that stalls leaves the toast at
      `opacity: 0` — in the DOM, announced to screen readers, but invisible on
      screen. Transitions stall whenever requestAnimationFrame is starved (a
      hidden/background tab is enough), which would silently swallow exactly the
      API errors this component exists to surface. Toasts therefore appear and
      disappear instantly.
    -->
    <div
      v-for="notification in ui.notifications"
      :key="notification.id"
      class="pointer-events-auto flex w-full max-w-md items-start gap-3 rounded-md border px-4 py-3 shadow-raised"
      :class="STYLES[notification.type].wrapper"
      role="alert"
      aria-live="assertive"
    >
      <AppIcon :name="STYLES[notification.type].icon" class="mt-0.5 text-lg" />
      <p class="flex-1 text-sm leading-snug font-medium">{{ notification.message }}</p>
      <button
        type="button"
        class="cursor-pointer rounded p-0.5 opacity-80 transition-opacity hover:opacity-100"
        :aria-label="t('common.close')"
        @click="ui.dismiss(notification.id)"
      >
        <AppIcon name="mdi-close" />
      </button>
    </div>
  </div>
</template>
